import { ROUTE_PREFIX } from 'util/Constants';

import { INVESTIGATION_STATUS_LABEL } from '../Losses/constants';

/**
 * The ADM review screen's vocabulary.
 *
 * Shared pieces — severity colours, confidence, amount formatting, the
 * rejection-reason floor — are imported from the Losses module rather than
 * copied. The two screens read one financial register, and a severity badge
 * that meant different things on each half would be worse than no badge.
 */
export {
  CONFIDENCE,
  CONFIDENCE_OPTIONS,
  INVESTIGATION_STATUS_LABEL,
  REJECT_REASON_MIN_LENGTH,
  SEVERITY_COLORS,
  SEVERITY_EMOJI,
  SEVERITY_OPTIONS,
  formatAmount,
} from '../Losses/constants';

/** Mirrors AdmStatus in the backend's losses/constants.py. */
export const ADM_STATUSES = {
  INGESTED: 'INGESTED',
  QUEUED: 'QUEUED',
  INVESTIGATING: 'INVESTIGATING',
  INVESTIGATED: 'INVESTIGATED',
  APPROVED: 'APPROVED',
  NOT_INVESTIGABLE: 'NOT_INVESTIGABLE',
  FAILED: 'FAILED',
};

export const ADM_STATUS_OPTIONS = [
  // INGESTED keeps its own word. It is the one ADM state with no loss-side
  // counterpart — nobody reports an ADM, it arrives — and the filter is more
  // useful naming that than folding it into "Queued" as the register does.
  { value: ADM_STATUSES.INGESTED, label: 'Ingested' },
  { value: ADM_STATUSES.QUEUED, label: INVESTIGATION_STATUS_LABEL.QUEUED },
  { value: ADM_STATUSES.INVESTIGATING, label: INVESTIGATION_STATUS_LABEL.INVESTIGATING },
  { value: ADM_STATUSES.INVESTIGATED, label: INVESTIGATION_STATUS_LABEL.INVESTIGATED },
  { value: ADM_STATUSES.APPROVED, label: INVESTIGATION_STATUS_LABEL.APPROVED },
  { value: ADM_STATUSES.NOT_INVESTIGABLE, label: INVESTIGATION_STATUS_LABEL.NOT_INVESTIGABLE },
  { value: ADM_STATUSES.FAILED, label: INVESTIGATION_STATUS_LABEL.FAILED },
];

export const ADM_STATUS_LABELS = ADM_STATUS_OPTIONS.reduce(
  (labels, option) => ({ ...labels, [option.value]: option.label }),
  {}
);

export const ADM_STATUS_TAG = {
  [ADM_STATUSES.INGESTED]: 'queued',
  [ADM_STATUSES.QUEUED]: 'queued',
  [ADM_STATUSES.INVESTIGATING]: 'progress',
  [ADM_STATUSES.INVESTIGATED]: 'pending',
  [ADM_STATUSES.APPROVED]: 'approved',
  [ADM_STATUSES.NOT_INVESTIGABLE]: 'manual',
  [ADM_STATUSES.FAILED]: 'failed',
};

/** Whose words a group's findings are. Mirrors InvestigationAuthorKind. */
export const FINDING_SOURCE = {
  AGENT: 'AGENT',
  HUMAN: 'HUMAN',
};

/**
 * Statuses a reviewer can act on.
 *
 * The server is what makes this true — it returns 409 for anything else. This
 * only decides whether to render the buttons, so a reviewer is not invited to
 * click something that will be refused.
 */
export const REVIEWABLE_STATUSES = [ADM_STATUSES.INVESTIGATED, ADM_STATUSES.NOT_INVESTIGABLE];

export const isAwaitingReview = (adm) => REVIEWABLE_STATUSES.includes(adm?.status);

/**
 * An ADM the agent could not investigate cannot simply be approved — there is
 * no conclusion to sign off. Writing one by hand is the way out, which is why
 * the detail screen opens such an ADM straight into edit mode.
 */
export const needsWrittenConclusion = (adm) => adm?.status === ADM_STATUSES.NOT_INVESTIGABLE;

export const buildAdmQueuePath = () => `${ROUTE_PREFIX}/admin-panel/adm`;

/**
 * The router pattern behind both of the paths below.
 *
 * One route serves the queue and the deep link, because they render the same
 * screen: `/admin-panel/adm` is the queue, `/admin-panel/adm/<id>` is the queue
 * with that memo's modal open. Exported so `routes/routes.js` and the paths
 * built here cannot drift — if the pattern stopped matching the deep link, the
 * Chat alerts already sent would land on a 404.
 */
export const ADM_ROUTE_PATTERN = `${ROUTE_PREFIX}/admin-panel/adm/:admId?`;

/**
 * The deep link to one ADM.
 *
 * There is no detail page any more — an ADM opens in a modal over the queue —
 * but this URL still resolves: the queue renders it and opens that memo's
 * modal. It has to, because it is the link the Google Chat investigation alert
 * sends (`chat.adm_review_link`) and alerts already delivered carry it.
 */
export const buildAdmDetailPath = (admId) => `${ROUTE_PREFIX}/admin-panel/adm/${admId}`;

export const buildOrderDetailPath = (orderId) => `${ROUTE_PREFIX}/admin-panel/booking-summary/${orderId}`;

/**
 * The register writes one row per PNR group and repeats the ADM total on each,
 * so a reader of the sheet needs this distinction too: column D is the
 * airline's figure and is authoritative, the group amounts are our own
 * apportionment and may legitimately not sum to it.
 */
export const reconciliationNote = (reconciliation) => {
  if (!reconciliation) return '';
  // camelCase, because `camelizeKeys` in admActions recurses into nested
  // objects — the server's `adm_total` never reaches a component under that
  // name. Reading the snake_case keys here made `total` undefined for every
  // ADM, so every one of them claimed to have no amount on its source row.
  const { admTotal: total, groupTotal: groups, delta } = reconciliation;
  if (total === null || total === undefined) return 'This ADM has no amount on its source row.';
  if (groups === null || groups === undefined) return 'No group carries an apportioned amount yet.';
  if (!delta) return 'The group amounts account for the airline’s full charge.';
  return delta > 0
    ? `PKR ${Number(delta).toLocaleString()} of the airline’s charge is not apportioned to any group.`
    : `The group amounts exceed the airline’s charge by PKR ${Math.abs(Number(delta)).toLocaleString()}.`;
};

/** One line per finding, as the register stores them. */
export const bulletsToText = (bullets) => (bullets || []).map((line) => String(line).replace(/^•\s*/, '')).join('\n');

export const textToBullets = (text) =>
  String(text || '')
    .split('\n')
    .map((line) => line.replace(/^•\s*/, '').trim())
    .filter(Boolean);
